import { Station } from '@app/core/types/stations';
import { AuditLiveMentionConfidence } from '@mi-front/core';
import { Box, Dialog, DialogContent } from '@mui/material';
import { getVerifyMatchTargetKey } from './get-verify-match-target-key';
import { useVerifyMatchDialogKeyboard } from './useVerifyMatchDialogKeyboard';
import {
  getVerifyMatchCreativeAudio,
  getVerifyMatchPlaybackRange,
} from './verify-match-target-utils';
import { VerifyMatchDialogNavigationState, VerifyMatchTarget } from './verify-match-types';
import { VerifyMatchDialogTitle } from './VerifyMatchDialogTitle';
import { VerifyMatchMentionConfidence } from './VerifyMatchMentionConfidence';
import { VerifyMatchPlayers } from './VerifyMatchPlayers';
import { VerifyMatchScheduleMeta } from './VerifyMatchScheduleMeta';
import { VerifyMatchTranscription } from './VerifyMatchTranscription';

export interface VerifyMatchDialogProps {
  open: boolean;
  onClose: () => void;
  station: Station;
  target: VerifyMatchTarget;
  navigation?: VerifyMatchDialogNavigationState;
  onMentionConfidenceChange?: (mentionId: string, confidence: AuditLiveMentionConfidence) => void;
  /** Optional broadcast video URL override (e.g. Storybook demo media). */
  broadcastVideoSrc?: string;
  /** Optional creative media URL override (e.g. Storybook demo media). */
  creativeMediaUrl?: string;
}

/**
 * Dialog to verify a spot or mention match with broadcast playback, reference media, and text.
 */
export function VerifyMatchDialog({
  open,
  onClose,
  station,
  target,
  navigation,
  onMentionConfidenceChange,
  broadcastVideoSrc,
  creativeMediaUrl,
}: VerifyMatchDialogProps) {
  const range = getVerifyMatchPlaybackRange(target);
  const creativeAudio = getVerifyMatchCreativeAudio(target);
  useVerifyMatchDialogKeyboard(open, navigation);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableEnforceFocus>
      <VerifyMatchDialogTitle
        station={station}
        target={target}
        onClose={onClose}
        navigation={navigation}
      />
      <DialogContent dividers sx={{ py: 3 }}>
        {open && (
          <Box
            key={getVerifyMatchTargetKey(target)}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            <VerifyMatchScheduleMeta target={target} />

            <VerifyMatchPlayers
              station={station}
              target={target}
              range={range}
              creativeAudio={creativeAudio}
              broadcastVideoSrc={broadcastVideoSrc}
              creativeMediaUrl={creativeMediaUrl}
            />

            <VerifyMatchTranscription
              target={target}
              stationId={station.id}
              timezone={station.timezone}
              open={open}
            />
          </Box>
        )}
      </DialogContent>
      {target.kind === 'mention' && (
        <VerifyMatchMentionConfidence
          key={getVerifyMatchTargetKey(target)}
          stationId={station.id}
          mention={target.mention}
          hotkeysEnabled={open}
          onConfidenceChange={onMentionConfidenceChange}
        />
      )}
    </Dialog>
  );
}
